import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import CashflowAreaDropdown from '../../../components/general/js/formTypes/cashflowAreaDropdown';
import SelectorEERR from '../../../components/general/js/formTypes/selectorEERR/selectorEERR';
import PlanDeCuentaDropdown from '../../../containers/PlanDeCuentaDropdown';
import { ModifyDocument } from '../../../graphql/documents';
import { importAlertState } from '../../../redux/actions/alert';
import { importUpdateCashflowNeeded } from '../../../redux/actions/flujocaja';
import { importEERRUpdateNeeded } from '../../../redux/actions/eerr';
import { setUpdateDocuments } from '../../../redux/actions/documentostributarios';
import { Plans } from '../../../utils/plans';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import './ParametrizacionTab.scss';

const ParametrizacionTab = ({ data }) => {
  const { t } = useTranslation('documents');
  const dispatch = useDispatch();
  const { selectedCompany } = useSelector((state) => state.companyList);
  const companyId = selectedCompany?.id;
  const companyPlans = selectedCompany?.plan || [];
  const isReceived = data?.meta?.isReceived;

  // Check if company has estado_de_resultados plan or full access plans
  const showEstadoResultados = companyPlans.includes(Plans.estado_de_resultados);
  const { hasModuleAccess } = useModulePermissions();
  const showCuentaContable = hasModuleAccess('plan_cuentas');
  const [isUpdatingCashflow, setIsUpdatingCashflow] = useState(false);
  const [isUpdatingFieldKey, setIsUpdatingFieldKey] = useState(false);
  const [isUpdatingAccountKey, setIsUpdatingAccountKey] = useState(false);
  const [localFieldKey, setLocalFieldKey] = useState(data?.fieldKey || '');
  const [localAccountKey, setLocalAccountKey] = useState(data?.accountKey || null);
  // Update local state when data prop changes
  useEffect(() => {
    setLocalFieldKey(data?.fieldKey || '');
  }, [data?.fieldKey]);

  useEffect(() => {
    setLocalAccountKey(data?.accountKey || null);
  }, [data?.accountKey]);

  const { executeMutation: modifyDocument } = ModifyDocument();

  const handleCashflowAreaChange = async (cashflowAreaId) => {
    if (!data?.id || !companyId) return;

    setIsUpdatingCashflow(true);
    try {
      const { data: result, error } = await modifyDocument({
        companyId,
        input: [{
          id: data.id,
          fields: [
            { key: 'cashflowAreaId', value: cashflowAreaId || null },
            { key: 'cashflowAreaIdModifiedByHand', value: true },
          ],
        }],
      });

      if (error || result?.modifyDocument?.nModified === 0) {
        dispatch(
          importAlertState({
            content: t('update.cashflowAreaError'),
            state: 'error',
            duration: 4,
          })
        );
      } else {
        dispatch(importUpdateCashflowNeeded({ value: true, reload: null }));
        dispatch(setUpdateDocuments({ value: true }));
      }
    } catch {
      dispatch(
        importAlertState({
          content: t('update.cashflowAreaUnexpected'),
          state: 'error',
          duration: 4,
        })
      );
    } finally {
      setIsUpdatingCashflow(false);
    }
  };

  const handleFieldKeyChange = async (_, { value }) => {
    if (!data?.id || !companyId) return;

    setIsUpdatingFieldKey(true);
    const newValue = value || 'none';
    setLocalFieldKey(newValue === 'none' ? '' : newValue);

    const fields = [{ key: 'fieldKey', value: newValue }];
    if (newValue !== 'none') {
      fields.push({ key: 'fieldKeyModifiedByHand', value: true });
    }

    try {
      const { data: result, error } = await modifyDocument({
        companyId,
        input: [{ id: data.id, fields }],
      });

      if (error || result?.modifyDocument?.nModified === 0) {
        setLocalFieldKey(data?.fieldKey || '');
        dispatch(
          importAlertState({
            content: t('update.genericError'),
            state: 'error',
            duration: 5,
          })
        );
      } else {
        dispatch(importEERRUpdateNeeded({ value: true }));
        dispatch(setUpdateDocuments({ value: true }));
      }
    } catch {
      setLocalFieldKey(data?.fieldKey || '');
      dispatch(
        importAlertState({
          content: t('update.eerrAreaUnexpected'),
          state: 'error',
          duration: 5,
        })
      );
    } finally {
      setIsUpdatingFieldKey(false);
    }
  };

  const handleAccountKeyChange = async (account) => {
    if (!data?.id || !companyId) return;
    const newKey = account?.key || null;
    setLocalAccountKey(newKey);
    setIsUpdatingAccountKey(true);
    try {
      const { data: result, error } = await modifyDocument({
        companyId,
        input: [{
          id: data.id,
          fields: [{ key: 'accountKey', value: newKey }],
        }],
      });
      if (error || result?.modifyDocument?.nModified === 0) {
        setLocalAccountKey(data?.accountKey || null);
        dispatch(importAlertState({ content: t('update.accountError'), state: 'error', duration: 4 }));
      } else {
        dispatch(setUpdateDocuments({ value: true }));
      }
    } catch {
      setLocalAccountKey(data?.accountKey || null);
      dispatch(importAlertState({ content: t('update.accountUnexpected'), state: 'error', duration: 4 }));
    } finally {
      setIsUpdatingAccountKey(false);
    }
  };

  return (
    <div className="parametrizacion-tab-content">
      <div className="parametrizacion-section">
        <div className="field">
          <label className="label">{t('update.cashflowAreaLabel')}</label>
          <div className="hint">{t('update.cashflowAreaHint')}</div>
          <CashflowAreaDropdown
            editData={{ cashflowAreaId: data?.cashflowAreaId }}
            onChange={handleCashflowAreaChange}
            enabled={!isUpdatingCashflow}
          />
        </div>

        {showEstadoResultados && (
          <div className="field">
            <label className="label">{t('update.eerrAreaLabel')}</label>
            <div className="hint">{t('update.eerrAreaHint')}</div>
            <SelectorEERR
              value={localFieldKey}
              onChange={handleFieldKeyChange}
              placeholder={t('update.selectArea')}
              companyId={companyId}
            />
          </div>
        )}

        {showCuentaContable && (
          <div className="field">
            <label className="label">{t('update.accountLabel')}</label>
            <div className="hint">
              {isReceived
                ? t('update.accountHintReceived')
                : t('update.accountHintIssued')}
            </div>
            <PlanDeCuentaDropdown
              value={localAccountKey || ''}
              onChange={(e, item) => handleAccountKeyChange(item?.account || null)}
              placeholder={isReceived ? t('update.accountPlaceholderReceived') : t('update.accountPlaceholderIssued')}
              disabled={isUpdatingAccountKey}
              portalMenu
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ParametrizacionTab;

